/**
 * The Secrets page's pure model (1.M-2 deliverables 1, 2, 3 and 5): the search the route keeps,
 * the rows of the paged list, each row's heading and state detail, and the debounce of the live
 * updates. Nothing here reads the network, the router or the DOM.
 */
import { DiscoveryState, type SecretV1, Uuid } from '@codex/contracts';

export type SecretState = DiscoveryState;

/** What `/secrets` keeps of its query string: a state filter, or one secret to show. */
export interface SecretsSearch {
  readonly state?: SecretState | undefined;
  readonly secret?: string | undefined;
}

/**
 * `?state=` when the ledger knows it, `?secret=` when it is a UUID; a secret drops the state.
 * Both keys are always in the answer, `undefined` when rejected: TanStack Router spreads this
 * answer over the raw search, so a key left out would let the raw value reach the API.
 */
export function parseSecretsSearch(raw: Record<string, unknown>): SecretsSearch {
  return { state: undefined, secret: undefined, ...pickSecretsSearch(raw) };
}

function pickSecretsSearch(raw: Record<string, unknown>): SecretsSearch {
  const secret = Uuid.safeParse(raw.secret);
  if (secret.success) return { secret: secret.data.toLowerCase() };
  const state = DiscoveryState.safeParse(raw.state);
  return state.success ? { state: state.data } : {};
}

/** The filter's links, in order; `undefined` is "All". The labels are the kit's. */
export const STATE_FILTERS: readonly {
  readonly state: SecretState | undefined;
  readonly label: string;
}[] = [
  { state: undefined, label: 'All' },
  { state: 'unknown', label: 'Unknown' },
  { state: 'partial', label: 'Partial' },
  { state: 'known', label: 'Known' },
];

/** Every page of the infinite query flattened, de-duplicated by id in server order. */
export function secretRows(pages: readonly { readonly items: readonly SecretV1[] }[]): SecretV1[] {
  const seen = new Set<string>();
  const rows: SecretV1[] = [];
  for (const page of pages) {
    for (const item of page.items) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      rows.push(item);
    }
  }
  return rows;
}

export const HEADING_MAX = 80;
export const UNTITLED_SECRET = 'Untitled secret';

/** A long first line cut at a word boundary to `max` characters, with "…". */
export function clip(line: string, max = HEADING_MAX): string {
  if (line.length <= max) return line;
  const room = line.slice(0, max - 1);
  const space = room.lastIndexOf(' ');
  const cut = space > 0 ? room.slice(0, space) : room;
  return `${cut.trimEnd()}…`;
}

/** A row's heading: the label, else the text's first line, else "Untitled secret". */
export function rowHeading(secret: Pick<SecretV1, 'label' | 'text'>): string {
  const label = secret.label?.trim() ?? '';
  if (label !== '') return label;
  const first = secret.text
    .split('\n')
    .map((line) => line.trim())
    .find((line) => line !== '');
  return first === undefined ? UNTITLED_SECRET : clip(first);
}

/**
 * The words after the state: since which session (its number when it has one, else its title),
 * or since before tracking began.
 */
export function stateDetail(
  secret: Pick<SecretV1, 'firstKnownSessionId' | 'knownBeforeTracking'>,
  titles: ReadonlyMap<string, string>,
  numbers: ReadonlyMap<string, number> = new Map(),
): string | undefined {
  if (secret.firstKnownSessionId !== null) {
    const number = numbers.get(secret.firstKnownSessionId);
    if (number !== undefined) return `since session ${number}`;
    const title = titles.get(secret.firstKnownSessionId);
    return title === undefined ? undefined : `since ${title}`;
  }
  return secret.knownBeforeTracking ? 'before tracking began' : undefined;
}

/** A secret's text longer than this is clamped to three lines, with "Show all". */
export const CLAMP_CHARS = 300;

export function needsClamp(text: string): boolean {
  return text.length > CLAMP_CHARS;
}

/** The page link's words: "On <title>", or "Was on <title>" once the secret left the page. */
export function pageLinkText(orphaned: boolean, title: string | undefined): string {
  const name = title ?? 'its page';
  return orphaned ? `Was on ${name}` : `On ${name}`;
}

/** The ids `docs.titles` resolves for one loaded page: documents and first-known sessions. */
export function titleIds(items: readonly SecretV1[]): string[] {
  const ids = new Set<string>();
  for (const item of items) {
    ids.add(item.documentId);
    if (item.firstKnownSessionId !== null) ids.add(item.firstKnownSessionId);
  }
  return [...ids].sort();
}

export const NO_SECRETS_TITLE = 'No secrets yet.';
export const NO_SECRETS_BODY = 'Wrap text in a secret block (/secret) and it appears here.';

/** The empty list's title: "No secrets yet." for All, else "No <label> secrets." */
export function emptyStateText(state: SecretState | undefined): string {
  if (state === undefined) return NO_SECRETS_TITLE;
  const label = STATE_FILTERS.find((f) => f.state === state)?.label ?? state;
  return `No ${label.toLowerCase()} secrets.`;
}

export interface Timers {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

const realTimers: Timers = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (handle) => globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>),
};

export interface Debounced {
  /** Asks for a run; the run happens `ms` after the last ask. */
  (): void;
  /** Drops a pending run. */
  cancel(): void;
  /** Runs a pending run now instead of later; nothing when none is pending. */
  runPending(): void;
}

/** `fn` once, `ms` after the last of a burst of calls. */
export function debounced(fn: () => void, ms: number, timers: Timers = realTimers): Debounced {
  let handle: unknown = null;
  const call = (() => {
    if (handle !== null) timers.clearTimeout(handle);
    handle = timers.setTimeout(() => {
      handle = null;
      fn();
    }, ms);
  }) as Debounced;
  call.cancel = () => {
    if (handle !== null) timers.clearTimeout(handle);
    handle = null;
  };
  call.runPending = () => {
    if (handle === null) return;
    call.cancel();
    fn();
  };
  return call;
}

/** What the list area shows: a failed "Show more" or refetch keeps the rows already loaded. */
export type ListBody = 'loading' | 'error' | 'empty' | 'rows';

export function listBody(list: {
  /** The list or the deep link has no answer yet. */
  readonly loading: boolean;
  /** At least one page has loaded. */
  readonly hasData: boolean;
  readonly rowCount: number;
}): ListBody {
  if (list.loading) return 'loading';
  if (!list.hasData) return 'error';
  return list.rowCount === 0 ? 'empty' : 'rows';
}

/**
 * The titles to show: while a page waits for its `docs.titles` answer (its ids changed, so its
 * query is new and has no data), the earlier titles carry under the new answers; once every page
 * has its answer, only the new answers count.
 */
export function carryTitles<T>(
  previous: ReadonlyMap<string, T>,
  fresh: ReadonlyMap<string, T>,
  complete: boolean,
): ReadonlyMap<string, T> {
  if (complete) return fresh;
  const carried = new Map(previous);
  for (const [key, value] of fresh) carried.set(key, value);
  return carried;
}
